// src/pages/AboutFAQPage.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Placeholder Icon for toggling
const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="value-prop-accordion-icon">
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

const AboutFAQPage = () => {
    
    // Framer Motion Variants for Staggered Entrance
    const container = {
        visible: {
            transition: { staggerChildren: 0.1 }
        }
    };
    
    const item = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

    // Converted AccordionItem to use Framer Motion for content transition
    const AccordionItem = ({ question, answer }) => {
        const [isOpen, setIsOpen] = useState(false);

        const toggleAccordion = () => {
            setIsOpen(!isOpen);
        };

        return (
            <motion.div 
                className={`value-prop-accordion-item ${isOpen ? 'value-prop-accordion-open' : ''}`}
                variants={item} // Apply staggered item entrance
            >
                
                {/* Accordion Summary (Clickable Header) */}
                <motion.button 
                    className="value-prop-accordion-summary" 
                    onClick={toggleAccordion}
                    aria-expanded={isOpen}
                    aria-controls={`answer-${question.replace(/\s/g, '-')}`}
                    whileHover={{ backgroundColor: '#f9f9f9' }}
                >
                    <span className="value-prop-accordion-question">{question}</span>
                    {/* Rotate the icon using Framer Motion's 'animate' property */}
                    <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="value-prop-accordion-icon"
                    >
                        <ChevronDownIcon />
                    </motion.span>
                </motion.button>

                {/* Accordion Details (Hidden Content) */}
                <AnimatePresence initial={false}>
                    {isOpen && (
                        <motion.div 
                            className="value-prop-accordion-details"
                            id={`answer-${question.replace(/\s/g, '-')}`}
                            // Use Framer Motion layout props for height animation (much smoother than CSS max-height)
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                                open: { opacity: 1, height: "auto" },
                                collapsed: { opacity: 0, height: 0 },
                            }}
                            transition={{ duration: 0.3, ease: [0.0, 0.7, 0.4, 1.0] }}
                        >
                            <p className="value-prop-accordion-answer">
                                {answer}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        );
    };

    return (
        <div className="public-page-container">
            <h1 className="public-page-title">About & FAQ</h1>
            <p className="public-page-subtitle">Your questions answered and our story told.</p>
            
            <div className="page-content-block" style={{ maxWidth: '800px' }}>
                <h3 className="widget-title" style={{ textAlign: 'left', marginBottom: '20px' }}>
                    Frequently Asked Questions
                </h3>
                
                <motion.div 
                    className="value-prop-accordion-container"
                    variants={container}
                    initial="hidden"
                    animate="visible"
                >
                    <AccordionItem 
                        question="How do you verify my address?" 
                        answer="We use a secure third-party service that cross-references your entered address with publicly available data without storing sensitive personal documents."
                    />
                    <AccordionItem 
                        question="Is this platform safe from spam?" 
                        answer="Yes. Every user must be verified, and we actively moderate all community forums to ensure discussions remain relevant and respectful."
                    />
                    <AccordionItem 
                        question="Which boroughs are supported?" 
                        answer="Currently, we support all five boroughs: Manhattan, Brooklyn, Queens, The Bronx, and Staten Island."
                    />
                </motion.div>
            </div>
        </div>
    );
};

export default AboutFAQPage;